// ABOUTME: The v28 side-view waʻa: the unfurled-wake sprite inline so its two sails can fold, and the furled sprite that fades in over it.
// ABOUTME: The caller owns the 48 x 48 box on the console centreline; both sprites are placed inside it as the asset manifest gives.

import { useLayoutEffect, useRef } from 'react';
import { Box } from '@mui/material';
import unfurledWake from './assets/waa-canoe-unfurled-wake.svg?raw';

// The two sail paths and the root size, found in the asset's own markup so a changed asset fails loudly.
const SAIL_PATHS = ['<path d="M83 100 Q92 60 94 23', '<path d="M118 97.4 Q127 57.4 129 20.4'];
const ROOT_SIZE = 'width="238" height="128"';

function markWithSails(svg: string): string {
  let marked = svg;
  for (const open of SAIL_PATHS) {
    if (!marked.includes(open)) throw new Error(`waa-canoe-unfurled-wake.svg has no sail path starting ${open}`);
    marked = marked.replace(open, open.replace('<path ', '<path class="waa-sail" '));
  }
  if (!marked.includes(ROOT_SIZE)) throw new Error('waa-canoe-unfurled-wake.svg has no 238 x 128 root size');
  return marked.replace(ROOT_SIZE, 'width="100%" height="100%"');
}

const UNFURLED_MARKUP = markWithSails(unfurledWake);
// React rewrites innerHTML whenever this object differs, so one shared object keeps the sail nodes and their style.
const UNFURLED_HTML = { __html: UNFURLED_MARKUP };

// Sail squeeze toward the mast: the same rotate/matrix/rotate the bird's-eye waʻa uses (screens/summary/AerialWaa.tsx).
const SAIL_MAST_LEAN = '5.4deg';
const sailSqueeze = (scaleX: number) => `rotate(${SAIL_MAST_LEAN}) matrix(${scaleX},0,0,1,0,0) rotate(-${SAIL_MAST_LEAN})`;
const SAIL_TRANSITION = 'transform 1.4s cubic-bezier(.55,.05,.45,1) 0.2s';
const FURLED_FADE = 'opacity 0.5s ease 1s';

interface WaaCanoeProps {
  sails: 'set' | 'furled';
  reducedMotion?: boolean;
}

export default function WaaCanoe({ sails, reducedMotion = false }: WaaCanoeProps) {
  const wakeRef = useRef<HTMLDivElement>(null);

  // React does not own the sail nodes (they come from the inlined markup), so their style is set here.
  useLayoutEffect(() => {
    wakeRef.current?.querySelectorAll<SVGPathElement>('.waa-sail').forEach((sail) => {
      sail.style.transformBox = 'fill-box';
      sail.style.transformOrigin = '77.5% 100%';
      sail.style.transition = reducedMotion ? 'none' : SAIL_TRANSITION;
      sail.style.transform = sailSqueeze(sails === 'furled' ? 0.08 : 1);
    });
  }, [sails, reducedMotion]);

  return (
    <>
      <Box
        ref={wakeRef}
        data-testid="waa-canoe-unfurled"
        aria-hidden="true"
        sx={{
          position: 'absolute', pointerEvents: 'none',
          '@keyframes bowFlow': { from: { strokeDashoffset: 0 }, to: { strokeDashoffset: -14 } },
          '@keyframes bowFlowLong': { from: { strokeDashoffset: 0 }, to: { strokeDashoffset: -197 } },
          // The asset's strokes carry their own inline animation; under reduced motion the wake stands still.
          ...(reducedMotion ? { '& *': { animation: 'none !important' } } : {}),
        }}
        style={{ left: '-82.4px', top: '-22px', width: '159px', height: '85.5px' }}
        dangerouslySetInnerHTML={UNFURLED_HTML}
      />
      <img
        data-testid="waa-canoe-furled"
        src="/crossing/waa-canoe-furled.svg"
        alt=""
        aria-hidden="true"
        style={{
          position: 'absolute', pointerEvents: 'none', left: '-47.6px', top: '-22px', width: '123px', height: '75px',
          opacity: sails === 'furled' ? 1 : 0,
          transition: reducedMotion ? 'none' : FURLED_FADE,
        }}
      />
    </>
  );
}
